import type { TenantConfig } from '@/types/index';
import { defaultTenant } from './_default/index';
import { alignmentTenant } from './alignment/index';
import { claridyneTenant } from './claridyne/index';

/**
 * Tenant resolution.
 *
 * Per spec §7: subdomain `{tenant}.reports.claridynamics.com` selects the tenant.
 * In dev, the host is `localhost` — fall back to a query param (?tenant=alignment)
 * or env var (VITE_TENANT_ID), defaulting to alignment for local development.
 *
 * Per Reporting_v4_Spec.md §4 Persona I (demo_operator): the demo_operator role
 * can only resolve tenants where `tenant_type='demo'`. That check happens in
 * AuthRoute / API handlers; this resolver just returns the requested tenant config.
 */

const TENANTS: Record<string, TenantConfig> = {
  _default: defaultTenant,
  alignment: alignmentTenant,
  claridyne: claridyneTenant,
};

/**
 * Exact-host → tenant map for aliased / custom domains that do NOT follow the
 * `{tenant}.reports.claridynamics.com` subdomain convention. The Alignment
 * portal is additionally served at `alignmenthealth.portal.cdxconnect.com`
 * (a domain AHP's FortiGuard filter already trusts, used to bypass its block of
 * claridynamics.com). That host's first label is `alignmenthealth`, which is
 * not a tenant key, so without this map resolveTenant would fall through to the
 * default tenant (empty programs, generic branding) on that host. Keyed by full
 * lowercase hostname.
 */
const HOST_TENANT: Record<string, string> = {
  'alignmenthealth.portal.cdxconnect.com': 'alignment',
  // AHP's Fortinet deep-inspects cdxconnect.com too (same ERR_CERT_AUTHORITY_INVALID),
  // but it EXEMPTS the Microsoft-owned default Static Web Apps host from inspection,
  // so the Alignment portal is additionally served on that host for AHP users. Its
  // first label (`proud-dune-01c4b9b0f`) is not a tenant key, so it needs this map.
  'proud-dune-01c4b9b0f.7.azurestaticapps.net': 'alignment',
  // Same pattern for the Claridyne DEMO: prospects behind firewalls that block
  // custom domains (e.g. BCBSM, 2026-07-01) get the demo on its raw Microsoft
  // host, which must resolve to the claridyne tenant for correct branding.
  //
  // BOTH demo hosts are mapped, deliberately (2026-08-05 rename):
  //   yellow-river   = swa-claridyne-reports-prod, the current demo SWA.
  //   wonderful-bush = swa-meridian-reports-prod, kept alive on purpose.
  //
  // wonderful-bush is not dead weight. The alignment.demo ClariLens link was
  // handed to AHP's architect hard-coded to wonderful-bush/clarilens/demo and
  // is embedded as a button on the LIVE Alignment client site, deliberately on
  // the Microsoft host because AHP's Fortinet SSL-inspects claridynamics.com.
  // It does not expire until 2027-06-30.
  //
  // That SWA still runs the PRE-rename build, and that keeps working, but the
  // mechanism is worth stating exactly because it is easy to get wrong:
  //
  //   - The campaign LIST comes from campaignFolders[] in the shared workbook
  //     (setup/campaigns.ts: "authoritative list, no directory-listing
  //     fallback"), NOT from the folder listing. After the push it returns the
  //     four Claridyne entries. That matters only because ClariLens Solo treats
  //     campaignCount === 0 as a degraded hydrate and shows a retry card
  //     instead of the chat. Non-empty is all it needs.
  //   - /api/ask does NOT use that list. It calls openCampaignDb on its own
  //     build-time slug, which resolves by slug over the folder listing
  //     (db-cache.ts resolveCampaignDir). The old build asks for
  //     "meridianfobt" and finds "05 Meridian FOBT".
  //
  // So the button survives exactly as long as the old campaign folders exist.
  // The uploader is additive and never deletes, so they do. Those stale folders
  // are therefore deliberately NOT cleaned up. Do not decommission that SWA,
  // and do not delete those folders, until AHP has repointed the button.
  // The map entry is kept so that if the Claridyne build ever IS deployed
  // there, it resolves correctly rather than falling through to the default.
  'yellow-river-02f9f020f.7.azurestaticapps.net': 'claridyne',
  'wonderful-bush-0cd65db0f.7.azurestaticapps.net': 'claridyne',
};

export function resolveTenant(): TenantConfig {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname.toLowerCase();
    // 0. Exact-host alias map (custom domains outside the subdomain convention)
    const mappedTenant = HOST_TENANT[host];
    if (mappedTenant && TENANTS[mappedTenant]) {
      return TENANTS[mappedTenant]!;
    }
    // 1. Subdomain check (production)
    const subdomain = host.split('.')[0];
    if (subdomain && TENANTS[subdomain]) {
      return TENANTS[subdomain]!;
    }
    // 2. ?tenant= query param (dev)
    const params = new URLSearchParams(window.location.search);
    const queryTenant = params.get('tenant');
    if (queryTenant && TENANTS[queryTenant]) {
      return TENANTS[queryTenant]!;
    }
  }
  // 3. Env var (dev)
  const envTenant = import.meta.env.VITE_TENANT_ID;
  if (envTenant && TENANTS[envTenant]) {
    return TENANTS[envTenant]!;
  }
  // 4. Default fallback (dev only — prod must hit a real subdomain)
  return import.meta.env.DEV ? alignmentTenant : defaultTenant;
}

/**
 * Short tenant code for UI copy (same idiom as Header/Footer: AHP for
 * Alignment, MHP for Claridyne, CDX otherwise). Use in example text and
 * chart footnotes so demo tenants never leak another plan's name.
 */
export function tenantShortCode(tenant: TenantConfig = resolveTenant()): string {
  return tenant.tenantId === 'alignment' ? 'AHP' : tenant.tenantId === 'claridyne' ? 'CHP' : 'CDX';
}

export { TENANTS };
